<template>
    <div class="top-container">
        <div class="left-column">
            <switch-shared v-if="isGodUser" class="left" :shared="shared" @shared-switched="switchMode" />
        </div>
        <div class="right-column ml-auto">
            <Button label="Go to Blocks" class="p-button-primary" @click="confirmNavigation" />
        </div>
    </div>
    <div class="shared" v-if="shared == 'Shared'">
        <div for="sharedHeading" class="text-center bg-red-500 text-yellow-400 font-bold">WARNING: Shared blocks will be saved publicly</div>
        <Divider />
    </div>
    <Accordion :activeIndex="activeTab" :disabled="completed">
        <AccordionTab header="General Information">
            <div class="grid">
                <div class="form-field col">
                    <label for="block-name">Block Name</label>
                    <InputText maxlength = "255" :class="{ 'p-invalid': submitted && !block.name }" v-model="block.name" placeholder="Enter block name" />
                    <small class="p-invalid" v-if="submitted && !block.name">Block Name is required.</small>
                </div>
                <div class="form-field col">
                    <label for="code">Code</label>
                    <InputText maxlength = "255" :class="{ 'p-invalid': submitted && !block.code }" v-model="block.code" placeholder="Enter unique block code" pattern="[a-zA-Z0-9]+" />
                    <small v-if="!codeIsValid" class="p-invalid">Please enter only alphanumeric characters.</small>
                    <small class="p-invalid" v-if="submitted && !block.code">Block Code is required.</small>
                </div>
                <div class="form-field col">
                    <label for="category">Category</label>
                    <Dropdown v-model="block.category_id" :options="categories" optionLabel="name" optionValue="id" placeholder="Select Category Type" showClear />
                </div>
            </div>
            <div class="form-field">
                <label for="description">Description</label>
                <Textarea rows="5" v-model="block.description" />
            </div>
            <div class="form-actions">
                <Button @click="nextTab" label="Next" />
            </div>
        </AccordionTab>
        <AccordionTab header="Block Builder" :disabled="!completed">
            <Button @click="completed = false" label="Previous" class="my-2" />
            <div class="container shadow-2 surface-card">
                <form-attributes :shared="shared" @dragged-item="draggedItem" class="shadow-3 surface-card form-elements" />
                <form-builder @drop="drop($event)" @dragover.prevent @show-design="showDesign" @attributes-block="addAttributesList" class="shadow-3 surface-card form-builder" />
                <form-designer :activeAttribute="activeAttribute" class="shadow-3 h-full surface-card form-designer" />
            </div>
        </AccordionTab>
    </Accordion>
    <div class="shared" v-if="shared == 'Shared'">
        <div for="sharedHeading" class="text-center bg-red-500 text-yellow-400 font-bold">WARNING: Shared blocks will be saved publicly</div>
        <Divider />
    </div>
</template>

<script>
import FormAttributes from './form/FormAttributes.vue';
import FormBuilder from './form/FormBuilder.vue';
import FormDesigner from './form/FormDesigner.vue';
import SwitchShared from '../SwitchShared.vue';

import { useRouter } from 'vue-router';
import { generateCode } from '@/helpers/text-formatter';
import { fetchAllCategories } from '@/services/category-service';
import { useBlockStore } from '../../products/pha/modules/block/store';
import { useBlockBuilderStore } from '@/store/block/block-builder';
import { mapGetters } from 'vuex';
import { mapStores } from 'pinia';
export default {
    name: 'AddBlock',
    components: { FormAttributes, FormBuilder, FormDesigner, SwitchShared },
    setup() {
        const blockStore = useBlockStore();
        const router = useRouter();
        return { blockStore, router };
    },
    data() {
        return {
            shared: this.$route.params.shared ?? 'Company',
            block: {},
            blockId: this.$route.params.id ?? null,
            activeTab: 0,
            completed: false,
            categories: null,
            submitted: false,
            selectedAttribute: null,
            activeAttribute: null,
        };
    },
    computed: {
        ...mapGetters('auth', ['userCompanyId', 'isGodUser']),
        ...mapStores(useBlockBuilderStore),
        codeIsValid() {
            return /^[a-zA-Z0-9]+$/.test(this.block.code);
        },
    },
    watch: {
        'block.name': {
            handler() {
                if (this.block.name) {
                    this.block.code = generateCode(this.block.code);
                } else {
                    this.block.code = null;
                }
            },
        },
        completed: {
            immediate: true,
            handler() {
                this.activeTab = this.completed ? 1 : 0;
            },
        },
        shared: {
            immediate: true,
            handler() {
                if (this.shared && this.shared == 'Company') {
                    this.block.company_id = this.userCompanyId.id;
                } else {
                    delete this.block.company_id;
                }
            },
        },
    },
    async created() {
        await this.fetchCategories();
        this.initBlockDetails();
    },
    methods: {
        async initBlockDetails() {
            if (this.blockId) {
                await this.blockStore.fetchBlockDetails(this.shared, this.blockId);
                // Setup initial block details
                this.loadBlockDetails(this.blockStore.block);
                // Setup attributes in proper format to view in FormBuilder
                this.loadAttributesFromDetails(this.blockStore.block?.attributes);
            } else {
                // else initialize block details with default values
                this.loadBlockDetails();
                // initialize null values in the block builder state just to avoid unwanted information
                this.loadAttributesFromDetails([]);
            }
        },
        loadBlockDetails(block = null) {
            if (!block?.id) {
                this.block = {
                    name: null,
                    code: null,
                    description: null,
                    category_id: null,
                    draft: true,
                    attributes_list: [],
                    company_id: this.userCompanyId.id,
                };
            } else {
                this.blockId = block.id;
                this.block = {
                    name: block.name,
                    code: block.code,
                    description: block.description,
                    category_id: block.category_id,
                    draft: false,
                    attributes_list: [],
                    company_id: this.userCompanyId.id,
                };
            }
        },
        loadAttributesFromDetails(attributes) {
            this.blockBuilderStore.setExistingAttributes(attributes);
        },
        async fetchCategories() {
            this.categories = await fetchAllCategories(this.userCompanyId.id);
        },

        nextTab() {
            if (this.isValid()) {
                // clicking next tab from general information will only save it as draft
                this.completed = true;
                // this.activeTab = 1;
                this.block.draft = true;
                if (!this.blockId) {
                    this.submit();
                }
            }
        },

        addAttributesList(attrArr) {
            // set draft to false since we are now saving block
            this.block.draft = false;
            this.block.attributes_list = attrArr;
            this.submit();
        },

        async submit() {
            if (this.isValid()) {
                //create initial block as a draft
                let newBlock = {};
                if (this.blockId) {
                    await this.blockStore.updateBlock(this.blockId, this.shared, this.block).then((updated) => {
                        if (updated) {
                            setTimeout(() => {
                                this.router.push({ name: 'Blocks' });
                            }, 1000);
                        }
                    });
                } else {
                    newBlock = await this.blockStore.createBlock(this.shared, this.block);
                    this.loadUpdatedBlockDetails(newBlock);
                }
            }
        },

        loadUpdatedBlockDetails(blockDetails) {
            this.blockId = blockDetails.id;
            this.block = {
                code: blockDetails.code,
                name: blockDetails.name,
                description: blockDetails.description,
                category_id: blockDetails.category_id,
                company_id: blockDetails.company_id,
                draft: false,
                attributes_list: blockDetails.attributes_list,
            };
        },

        isValid() {
            this.submitted = true;
            if (!this.block.code || !this.block.name || !this.codeIsValid) {
                return false;
            }
            return true;
        },

        confirmNavigation() {
            this.$confirm.require({
                header: 'Confirmation',
                message: 'Are you sure you want to go back to block index page?',
                icon: 'pi pi-exclamation-triangle',
                accept: async () => {
                    this.router.push({ name: 'Blocks' });
                },
                reject: () => {
                    this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Cancelled', life: 5000 });
                },
            });
        },

        switchMode(mode) {
            this.shared = mode;
        },
        draggedItem(attribute) {
            this.blockBuilderStore.selectedAttribute = attribute;
        },
        drop(event) {
            event.preventDefault();
            const droppedItem = { ...this.blockBuilderStore.selectedAttribute, uniqueId: Math.random().toString(36).substring(2, 9) };
            this.blockBuilderStore.droppedAttributes.push(droppedItem);
            this.blockBuilderStore.selectedAttribute = null;
        },
        // set active attribute to pass is as props to form designer component
        showDesign(attribute) {
            this.activeAttribute = attribute;
        },
    },
};
</script>

<style scoped>
.top-container {
    display: flex;
}

.left-column {
    padding: 1rem;
}

.right-column {
    padding: 1rem;
}
.container {
    display: flex;
    flex-direction: row;
    height: 80vh;
}
.container > div {
    padding: 1rem;
}

.form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
}

.form-field label {
    margin-bottom: 0.5rem;
}

.form-elements {
    width: 20%;
}
.form-builder {
    width: 45%;
}
.form-designer {
    width: 35%;
    height: 50vh;
}
</style>
